/**
 * ProductTour (#demo) — five real product demo clips behind an accessible
 * tablist (arrow keys / Home / End), one <DemoVideo> mounted at a time.
 */

import { useRef, useState, type KeyboardEvent } from 'react';

import { useT } from '@/i18n';
import type { VideoId } from '@/routes/landing/landing-media';
import { DemoVideo } from './DemoVideo';
import { PrimaryCta, SectionHeading, useLandingCta } from './shared';

const CLIPS = [
  { id: 'autopilot', tab: 'tabAutopilot', desc: 'descAutopilot' },
  { id: 'composer-calendar', tab: 'tabComposer', desc: 'descComposer' },
  { id: 'ai-team', tab: 'tabAiTeam', desc: 'descAiTeam' },
  { id: 'crm-email', tab: 'tabCrm', desc: 'descCrm' },
  { id: 'reel-studio', tab: 'tabReel', desc: 'descReel' },
] as const satisfies readonly { id: VideoId; tab: string; desc: string }[];

export function ProductTour() {
  const { t } = useT();
  const { signup } = useLandingCta();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const focusTab = (i: number) => {
    const next = (i + CLIPS.length) % CLIPS.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusTab(i + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusTab(i - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(CLIPS.length - 1);
    }
  };

  const clip = CLIPS[active];
  const tabLabel = t(`routes.landing.v2.tour.${clip.tab}`);

  return (
    <section id="demo" className="relative overflow-hidden bg-surface-2/40 py-16 sm:py-20 lg:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t('routes.landing.v2.tour.eyebrow')}
          title={t('routes.landing.v2.tour.title')}
          subtitle={t('routes.landing.v2.tour.subtitle')}
        />

        <div
          role="tablist"
          aria-label={t('routes.landing.v2.tour.tablist')}
          className="mt-10 flex flex-wrap justify-center gap-2"
        >
          {CLIPS.map((c, i) => {
            const selected = i === active;
            return (
              <button
                key={c.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`tour-tab-${c.id}`}
                aria-selected={selected}
                aria-controls="tour-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(i)}
                onKeyDown={(e) => onKeyDown(e, i)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${
                  selected
                    ? 'border-transparent bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-elevation-1'
                    : 'border-border bg-surface text-foreground hover:bg-surface-2'
                }`}
              >
                {t(`routes.landing.v2.tour.${c.tab}`)}
              </button>
            );
          })}
        </div>

        <div id="tour-panel" role="tabpanel" aria-labelledby={`tour-tab-${clip.id}`} className="mt-8">
          <DemoVideo id={clip.id} title={tabLabel} description={t(`routes.landing.v2.tour.${clip.desc}`)} />
        </div>

        <div className="mt-8 flex justify-center">
          <PrimaryCta onClick={() => signup()}>{t('routes.landing.v2.tour.tryIt')}</PrimaryCta>
        </div>
      </div>
    </section>
  );
}
